import type { UseQueryOptions } from "@tanstack/react-query";
import {
  cachedImageQueryOptions,
  type CachedImageSource,
} from "@/shared/api/hooks/use-cached-image-asset";

/** A floorplan raster's stable logical identity within a deployment. */
export interface FloorplanImageIdentity {
  deploymentId: string;
  floorplanId: string;
  revision: number;
}

/**
 * Builds a stable, same-origin synthetic Cache Storage key for a floorplan
 * raster from its logical identity, independent of the temporary signed URL
 * used to fetch it. The revision is encoded as the query string (not the
 * path) so `fetchCachedAsset`'s existing path-based stale-version eviction
 * evicts exactly the prior revision of the same floorplan when the revision
 * changes, without evicting unrelated floorplans.
 *
 * Never derived from, and never contains, the signed source URL itself.
 */
export function buildFloorplanCacheKey({
  deploymentId,
  floorplanId,
  revision,
}: FloorplanImageIdentity): string {
  return new URL(
    `/__cache/floorplans/${encodeURIComponent(deploymentId)}/${encodeURIComponent(floorplanId)}?revision=${revision}`,
    window.location.origin
  ).href;
}

/** TanStack Query key for a floorplan raster, shared by preload and selected-area consumption. */
export function floorplanImageQueryKey({
  deploymentId,
  floorplanId,
  revision,
}: FloorplanImageIdentity): readonly unknown[] {
  return ["floorplan-image", deploymentId, floorplanId, revision];
}

/**
 * The single reusable query definition for a floorplan raster, used by both
 * the background deployment preloader (via `useQueries`, never creating an
 * object URL) and the selected schematic's `useCachedImageAsset` consumer
 * (which additionally owns an object URL for rendering). `sourceUrl` is the
 * area map response's temporary signed image URL, used only on a network
 * miss; the query/cache identity itself is `deploymentId + floorplanId +
 * revision`, so a re-signed URL for an already-cached revision never causes
 * a refetch.
 */
export function floorplanImageQueryOptions(
  identity: FloorplanImageIdentity,
  sourceUrl: string | undefined
): UseQueryOptions<CachedImageSource> {
  return cachedImageQueryOptions(identity.deploymentId, sourceUrl, {
    cacheKey: buildFloorplanCacheKey(identity),
    queryKey: floorplanImageQueryKey(identity),
  });
}
